import { useState, useEffect, useMemo } from 'react'
import { useWcbApi } from './use-wcb-api'
import { useCanadianSubdivisions } from './use-canadian-subdivisions'
import { CalculatorProvince, WorkerCompensationItemWithDisplay } from '@/lib/types/calculator'
import { WorkerCompensationItem } from '@/lib/config'

interface UseCalculatorDataProps {
  provinceId?: string
  subdivisionId?: number
}

export const useCalculatorData = ({ provinceId, subdivisionId }: UseCalculatorDataProps) => {
  // WCB data state
  const [wcbData, setWcbData] = useState<WorkerCompensationItemWithDisplay[]>([])
  const [wcbLoading, setWcbLoading] = useState(false)
  const [wcbError, setWcbError] = useState<string | null>(null)
  
  const wcbApi = useWcbApi()
  
  // Use the new Canadian subdivisions hook instead of loading manually
  const {
    provinces,
    loading: provincesLoading,
    error: provincesError
  } = useCanadianSubdivisions()

  // Load WCB data for a specific subdivision
  const loadWcbData = async (targetSubdivisionId: number) => {
    setWcbLoading(true)
    setWcbError(null)

    try {
      const response = await wcbApi.getWorkerCompensationsBySubdivision(targetSubdivisionId.toString())
      const wcbItems = response.Items || []

      // Map response to include display text
      const mappedItems: WorkerCompensationItemWithDisplay[] = wcbItems.map((item: WorkerCompensationItem) => ({
        ...item,
        DisplayText: `${item.Code} - ${item.Name}`
      }))

      setWcbData(mappedItems)

    } catch (error) {
      console.error('Failed to load WCB data for subdivision:', targetSubdivisionId, error)
      setWcbError('Failed to load worker compensation data for selected province')
      setWcbData([])
    } finally {
      setWcbLoading(false)
    }
  }

  // Get current subdivision ID based on province and initial data
  const getCurrentSubdivisionId = () => {
    if (subdivisionId && provinceId) {
      // Use provided subdivision ID if it matches the current province
      const province = provinces.find((p: CalculatorProvince) => p.id === provinceId)
      if (province && subdivisionId === province.subdivisionId) {
        return subdivisionId
      }
    }

    // Fall back to province-based lookup
    const province = provinces.find((p: CalculatorProvince) => p.id === provinceId)
    return province?.subdivisionId || 600 // Default to Ontario
  }

  // Load WCB data when province changes
  useEffect(() => {
    if (!provinces.length || !provinceId) {
      return // Don't load if we don't have the required data
    }

    const currentSubdivisionId = getCurrentSubdivisionId()
    if (!currentSubdivisionId) {
      return
    }

    // Only load if we don't already have data for this subdivision
    const hasDataForSubdivision = wcbData.some(item => item.SubdivisionId === currentSubdivisionId)
    if (hasDataForSubdivision) {
      return
    }

    loadWcbData(currentSubdivisionId)
  }, [provinceId, subdivisionId, provinces, wcbApi, wcbData])

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => ({
    // Province data
    provinces,
    provincesLoading,
    provincesError,
    
    // WCB data
    wcbData,
    wcbLoading,
    wcbError,
    
    // Helper functions
    getCurrentSubdivisionId,
    reloadWcbData: (subdivisionId: number) => loadWcbData(subdivisionId)
  }), [
    provinces,
    provincesLoading,
    provincesError,
    wcbData,
    wcbLoading,
    wcbError,
    getCurrentSubdivisionId
  ])
}; 